import { useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import { useCrmLeadsExportPanel } from "app/pages/sales-intelligence/modules/leads-search/hooks/useCrmLeadsExportPanel";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import LegacyWebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/LegacyWebsitesCRMExportPanel/LegacyWebsitesCRMExportPanelView";
import WebsitesCRMExportPanelView from "app/pages/sales-intelligence/sub-modules/management/components/WebsitesCRMExportPanel/WebsitesCRMExportPanelView";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { useCurrentOpportunityListCountry } from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentOpportunityListCountry";
import type { Translatable } from "app/pages/sales-intelligence/types";

import { useLeadsResultsActionsState } from "../../../hooks/useLeadsResultsActionsState";

type Props = BaseActivatedPanelProps & {
    integration: Integration;
    isExportInProgress: boolean;
    label: Readonly<Translatable>;
    onExportNDomains: (domainsCount: number, countries: readonly number[]) => void;
    onExportDomains: (domains: readonly string[], countries: readonly number[]) => void;
};

const CrmExportPanelContainer = ({
    integration,
    isExportInProgress,
    label,
    onExportNDomains,
    onExportDomains,
    onDeactivate,
}: Props) => {
    const listCountry = useCurrentOpportunityListCountry();

    const activeLeadsType = useSelector(selectActiveLeadsType);

    const { areActionsDisabled, numberOfResults, areResultsFetching } = useLeadsResultsActionsState(activeLeadsType);

    const { excelQuota, selectedRowDomains, leadsSelectionDropdown, onClearAndDeactivate, onSelectionChange } =
        useCrmLeadsExportPanel(onDeactivate);

    const handleSubmit = (item: Readonly<SelectionDropdownItem>) => {
        trackingService.trackMultiSelectorExportClick(integration, excelQuota.remaining, item.alias, item.value);

        onClearAndDeactivate();

        if (item.id === ManualSelectionItem.ID) {
            return onExportDomains(selectedRowDomains, [listCountry]);
        }

        return onExportNDomains(item.value, [listCountry]);
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesCRMExportPanelView
                isDisabled={isExportInProgress || areActionsDisabled}
                isLoading={isExportInProgress || areResultsFetching}
                label={label}
                numberOfSelectedItems={selectedRowDomains.length}
                numberOfRemainingExports={excelQuota.remaining}
                numberOfResults={numberOfResults}
                selectionDropdown={leadsSelectionDropdown}
                onDeactivate={onClearAndDeactivate}
                onSelectionChange={onSelectionChange}
                onSubmit={handleSubmit}
                exportType={integration === "hubspot" ? ExportCostTypeItem.Hubspot : integration === "dynamics" ? ExportCostTypeItem.Dynamics : ExportCostTypeItem.Salesforce}
            />
        );
    }

    return (
        <LegacyWebsitesCRMExportPanelView
            isDisabled={isExportInProgress || areActionsDisabled}
            isLoading={isExportInProgress || areResultsFetching}
            label={label}
            numberOfSelectedItems={selectedRowDomains.length}
            numberOfRemainingExports={excelQuota.remaining}
            numberOfResults={numberOfResults}
            selectionDropdown={leadsSelectionDropdown}
            onDeactivate={onClearAndDeactivate}
            onSelectionChange={onSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default CrmExportPanelContainer;
